import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  CircularProgress,
  Button,
  Tooltip,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import TextMessage from "./TextMessage";
import WhatsappInteractiveBlocks, { WhatsappInboundChoiceFooter } from "./WhatsappInteractiveBlocks";
import ImageMessage from "./ImageMessage";
import AudioMessage from "./AudioMessage";
import VideoMessage from "./VideoMessage";
import DocumentMessage from "./DocumentMessage";
import LocationMessage from "./LocationMessage";
import LogMessage from "./LogMessage";
import InteractiveMessage from "./InteractiveMessage";
import {
  getDeliveryStatusDisplay,
  shouldShowDeliveryStatus,
  isReengagementError,
  REENGAGEMENT_FRIENDLY_MESSAGE,
  getFriendlyDeliveryError,
} from "./messageDeliveryStatus";
import { formatWhatsAppText } from "../../utils/whatsappTextFormat";
import { parseRawEmail } from "../../utils/parseRawEmail";
import {
  getMessageThreadHints,
  resolveQuotedPreview,
  getWhatsappForwardBannerLabel,
} from "../../utils/messageThreadHints";
import {
  buildWhatsappInteractivePresentation,
  isTechnicalWhatsappChoiceId,
} from "../../utils/whatsappInteractiveDisplay";

/** Formata datas no padrão do xpro antigo (America/Araguaina) */
const parseDisplayDate = (value) => {
  if (!value) return "—";

  // Formato MySQL vindo do backend: "YYYY-MM-DD HH:mm:ss"
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "0000-00-00 00:00:00") return "—";
    const mysqlRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

    if (mysqlRegex.test(trimmed)) {
      // No xpro antigo, esse valor já está no fuso America/Araguaina (sem conversões).
      // Mantemos o horário e apenas reformatamos para DD/MM/YYYY HH:mm:ss (sem vírgula).
      const [datePart, timePart] = trimmed.split(" ");
      const [year, month, day] = datePart.split("-");
      return `${day}/${month}/${year} ${timePart}`;
    }
  }

  // Para datas JS/ISO, usa locale brasileiro (aproxima do antigo comportamento)
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";

  return d.toLocaleString("pt-BR");
};

/**
 * Tipos de mensagem para alinhamento e estilo:
 * - Cliente: mensagem do cliente/BOT → esquerda, balão neutro
 * - Usuário logado: id_usuario/senderId da mensagem = id do usuário logado → direita, balão destacado
 * - Outros usuários: agent/operator mas não é o logado → direita, balão cor diferente
 * - WhatsApp (app): enviado pelo app no smartphone (canal), sem usuário Xpro → direita, balão “outros”
 */
const getSenderFlags = (message, user) => {
  const senderType = (message?.senderType || "").toLowerCase();
  const sender = (message?.sender || "").toLowerCase();

  const isSystem = sender === "system" || senderType === "system";

  const isClient =
    sender === "cliente" ||
    sender === "client" ||
    senderType === "client" ||
    senderType === "customer" ||
    message?.channel === "customer";

  const isWhatsappAppOutbound = senderType === "whatsapp_app";

  const isAgentOrOperator =
    sender === "agent" ||
    sender === "operator" ||
    senderType === "agent" ||
    senderType === "operator";

  const loggedUserId = user?.id != null ? String(user.id) : null;
  const msgUserId =
    message?.senderId != null
      ? String(message.senderId)
      : message?.id_usuario != null
      ? String(message.id_usuario)
      : null;

  const isLoggedInUser =
    !!loggedUserId && !!msgUserId && msgUserId === loggedUserId;

  const isOtherUser = !!user && !isClient && !isSystem && isAgentOrOperator && !isLoggedInUser;
  const isUser = isLoggedInUser || isOtherUser || isWhatsappAppOutbound;

  return { isClient, isSystem, isUser, isLoggedInUser, isOtherUser, isWhatsappAppOutbound };
};

/** Label do canal exibido após "via ...". Usa o canal de cada mensagem (channelName/channel); fallback no canal da solicitação só quando a mensagem não tem ou é genérico (Xpro, Call). */
const getChannelLabel = (message, defaultChannel) => {
  const msgType = (message?.type || "").toString().toLowerCase();

  // Anotações e logs não exibem canal.
  if (msgType === "note" || msgType === "log" || msgType === "xpro") {
    return "";
  }

  const baseFallback = (defaultChannel ?? "").toString().trim();
  const raw = (message?.channelName ?? message?.channel ?? "").toString().trim();

  if (!raw) {
    return baseFallback;
  }

  const lower = raw.toLowerCase();

  // Valores genéricos: usar nome do canal da solicitação em vez de "unknown", "Xpro" ou "Call".
  if (lower === "unknown" || lower === "desconhecido" || lower === "xpro" || lower === "call") {
    return baseFallback || (lower === "call" ? "Call" : lower === "xpro" ? "Xpro" : "");
  }

  // Canais técnicos de mídia: nome da mensagem ou fallback.
  if (["imagem", "image", "video", "audio", "document"].includes(lower)) {
    return baseFallback || "WhatsApp";
  }

  // Normalização de valores comuns.
  if (lower.startsWith("whatsapp")) return "WhatsApp";
  if (lower === "email") return "Email";
  if (lower === "sms") return "SMS";
  if (lower === "phone" || lower === "telefone") return "Phone";

  return raw;
};

/** Rótulos em PT-BR para cabeçalhos do e-mail */
const EMAIL_HEADER_LABELS = {
  from: "De",
  to: "Para",
  cc: "Cc",
  bcc: "Cópia oculta",
  "reply-to": "Responder a",
  subject: "Assunto",
  date: "Data",
};

/** Popup que exibe o e-mail original de forma legível (cabeçalhos + corpo), não em código bruto. */
function EmailOriginalDialog({ open, onClose, rawEmail }) {
  const parsed = React.useMemo(
    () => (open && rawEmail ? parseRawEmail(rawEmail) : null),
    [open, rawEmail]
  );

  const hasVisibleContent =
    parsed &&
    (Object.keys(parsed.friendlyHeaders || {}).length > 0 ||
      (parsed.bodyHtml && parsed.bodyHtml.trim()) ||
      (parsed.bodyText && parsed.bodyText.trim()));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: "85vh" } }}>
      <DialogTitle>E-mail original</DialogTitle>
      <DialogContent>
        {parsed && hasVisibleContent ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {Object.keys(parsed.friendlyHeaders).length > 0 && (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  "& > div": { mb: 0.5 },
                  "& > div:last-of-type": { mb: 0 },
                }}
              >
                {Object.entries(parsed.friendlyHeaders).map(([key, value]) => (
                  <Box key={key} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600, minWidth: "90px" }}>
                      {EMAIL_HEADER_LABELS[key] || key}:
                    </Typography>
                    <Typography component="span" variant="body2" sx={{ wordBreak: "break-word" }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {(parsed.bodyHtml || parsed.bodyText) && (
              <Box
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "auto",
                  maxHeight: "50vh",
                }}
              >
                {parsed.bodyHtml ? (
                  <Box
                    component="iframe"
                    title="Corpo do e-mail"
                    srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:8px;font-family:inherit;font-size:14px;">${parsed.bodyHtml.replace(/<\/body\s*>/gi, "&lt;/body&gt;").replace(/<\/html\s*>/gi, "&lt;/html&gt;")}</body></html>`}
                    sandbox="allow-same-origin"
                    sx={{
                      width: "100%",
                      minHeight: "200px",
                      border: "none",
                      display: "block",
                    }}
                  />
                ) : (
                  <Typography
                    component="pre"
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      m: 0,
                      fontFamily: "inherit",
                    }}
                  >
                    {parsed.bodyText}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Box
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              fontSize: "0.8rem",
              p: 1.5,
              bgcolor: "action.hover",
              borderRadius: 1,
              overflow: "auto",
              maxHeight: "60vh",
            }}
          >
            {rawEmail || ""}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            try {
              navigator.clipboard.writeText(rawEmail || "");
            } catch (_) {}
          }}
        >
          Copiar
        </Button>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

const MessageItem = ({
  message,
  user,
  onSendMessage,
  defaultChannel,
  quotedLookup,
  onNavigateToQuotedMessage,
  onDeleteMessage,
  conversationMessages,
  messageIndex,
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const ref = useRef();
  const [emailOriginalOpen, setEmailOriginalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [, setDeleteWindowTick] = useState(0);

  const { isClient, isSystem, isUser, isLoggedInUser, isOtherUser, isWhatsappAppOutbound } = getSenderFlags(
    message,
    user
  );
  const agentSideVisual = isOtherUser || isWhatsappAppOutbound;
  const hasEmailOriginal = !!(message?.emailOriginal && String(message.emailOriginal).trim());

  // Identificação visual por tipo de automação:
  // - Agente de IA: respostas automáticas (tipo 35 / AutoReply).
  // - Chatbot: mensagem atribuída a usuário de tipo chatbot, quando não for AutoReply.
  const isAiAgentMessage = message?.type === "automaticanswer" || message?.channel === "AutoReply";
  const isChatbotMessage =
    !isAiAgentMessage &&
    ((String(message?.senderType || "").toLowerCase() === "agent") ||
      (String(message?.sender || "").toLowerCase() === "agent")) &&
    Number(message?.uraUser) === 1;

  const whatsappMeta = React.useMemo(() => {
    const raw = message?.json_whatsapp;
    if (!raw) return null;
    if (typeof raw === "object") return raw;
    if (typeof raw !== "string") return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [message?.json_whatsapp]);

  const threadHints = React.useMemo(
    () => getMessageThreadHints(message),
    [message?.json_whatsapp, message?.emailOriginalTags]
  );

  const quotedPreview = React.useMemo(() => {
    if (!quotedLookup) return null;
    return resolveQuotedPreview(message, quotedLookup, user);
  }, [quotedLookup, message, user, message?.id, message?.json_whatsapp, message?.emailOriginalTags]);

  const waChannelBlob = `${message?.channel || ""} ${defaultChannel || ""}`;
  const isLikelyWhatsapp = /\bwhatsapp\b/i.test(waChannelBlob);

  const waInteractivePres = React.useMemo(() => {
    if (!isLikelyWhatsapp || !Array.isArray(conversationMessages) || messageIndex == null) return null;
    return buildWhatsappInteractivePresentation(message, messageIndex, conversationMessages);
  }, [isLikelyWhatsapp, message, message?.id, message?.json_whatsapp, message?.text, messageIndex, conversationMessages]);

  const waClientTextOverride = React.useMemo(() => {
    if (!isLikelyWhatsapp || !isClient || !waInteractivePres) return undefined;
    const t = message?.text != null ? String(message.text).trim() : "";
    if (!t) return undefined;
    const { inboundHumanFromPayload, inboundChoice, numberedFallbackLabel } = waInteractivePres;
    if (isTechnicalWhatsappChoiceId(t)) {
      const better =
        (inboundHumanFromPayload && inboundHumanFromPayload !== t ? inboundHumanFromPayload : "") ||
        (inboundChoice?.title && inboundChoice.title !== t ? inboundChoice.title : "") ||
        numberedFallbackLabel ||
        "";
      return better || undefined;
    }
    if (inboundChoice?.id && inboundChoice.title && t === inboundChoice.id && inboundChoice.title !== t) {
      return inboundChoice.title;
    }
    return undefined;
  }, [isLikelyWhatsapp, isClient, waInteractivePres, message?.text]);

  const isOutboundFromWhatsappApp =
    !isClient &&
    whatsappMeta?.xpro_origin === "whatsapp_app" &&
    whatsappMeta?.xpro_direction === "outbound_external";

  /** Reações recebidas via webhook (Meta / Gupshup / API Fácil), persistidas em json_whatsapp.xpro_reactions */
  const waForwardLabel = React.useMemo(
    () => (isLikelyWhatsapp ? getWhatsappForwardBannerLabel(message) : null),
    [isLikelyWhatsapp, message?.json_whatsapp, message?.isForwardedWhatsapp]
  );

  const waReactionChips = React.useMemo(() => {
    if (!isLikelyWhatsapp || !whatsappMeta || typeof whatsappMeta !== "object") return null;
    const xr = whatsappMeta.xpro_reactions;
    if (!xr || typeof xr !== "object" || !xr.byReactor || typeof xr.byReactor !== "object") return null;
    const entries = Object.values(xr.byReactor).filter((v) => v && v.emoji && String(v.emoji).trim());
    if (!entries.length) return null;
    const counts = new Map();
    for (const v of entries) {
      const em = String(v.emoji).trim();
      counts.set(em, (counts.get(em) || 0) + 1);
    }
    return Array.from(counts.entries()).map(([emoji, n], idx) => ({
      key: `${emoji}-${idx}`,
      label: n > 1 ? `${emoji} · ${n}` : emoji,
      tooltip: n > 1 ? `${n} participantes reagiram com ${emoji}` : "Reação no WhatsApp",
    }));
  }, [isLikelyWhatsapp, whatsappMeta]);

  const getAlignment = () => {
    if (message.type === "log" || message.type === "note" || message.type === "Xpro") return "center";
    if (isUser) return "flex-end";
    if (isClient) return "flex-start";
    return "flex-start";
  };

  const getMessageStyle = () => {
    if (message.type === "log") {
      const logBg = mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100];
      return {
        backgroundColor: logBg,
        borderRadius: "14px",
        padding: "8px 14px",
        maxWidth: "100%",
        color: theme.palette.text.primary,
        textAlign: "center",
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        boxShadow:
          mode === "dark"
            ? "0px 1px 3px rgba(0, 0, 0, 0.3)"
            : "0px 1px 3px rgba(0, 0, 0, 0.08)",
      };
    }

    if (message.type === "note" || message.type === "Xpro") {
      return {
        backgroundColor: theme.palette.messages?.notes || theme.palette.action.hover,
        color: theme.palette.text.primary,
        borderLeft: `4px solid ${theme.palette.warning.main}`,
        borderRadius: "8px",
        padding: "12px",
        maxWidth: "100%",
        boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
        textAlign: "center"
      };
    }

    // Três estilos: cliente (esquerda, neutro) | usuário logado (direita, destacado) | outros usuários (direita, cor diferente)
    const isDark = mode === "dark";
    const clientBg = theme.palette.messages?.client ?? (isDark ? "#3a3f44" : "#e8ecf0");
    const clientColor = theme.palette.messages?.clientContrast ?? (isDark ? "#e4e6eb" : "#1c1e21");
    const loggedInUserBg = theme.palette.messages?.user ?? (isDark ? "#0b93f6" : "#0b93f6");
    const loggedInUserColor = theme.palette.messages?.userContrast ?? "#ffffff";
    const otherUserBg = theme.palette.messages?.others ?? (isDark ? "#4a5568" : "#d4dce4");
    const otherUserColor = theme.palette.messages?.othersContrast ?? (isDark ? "#e4e6eb" : "#1c1e21");

    const bg = isLoggedInUser ? loggedInUserBg : agentSideVisual ? otherUserBg : clientBg;
    const fg = isLoggedInUser ? loggedInUserColor : agentSideVisual ? otherUserColor : clientColor;

    return {
      backgroundColor: bg,
      color: fg,
      borderRadius: "12px",
      padding: "12px",
      maxWidth: "100%",
      boxShadow: isLoggedInUser
        ? "0px 1px 2px rgba(0, 0, 0, 0.2)"
        : "0px 1px 2px rgba(0, 0, 0, 0.08)",
      border: isLoggedInUser ? "none" : undefined,
    };
  };

  /** Renderiza mídia (imagem, vídeo, áudio, documento, localização) quando presente, para qualquer tipo de mensagem.
   *  Para evitar duplicidade, o tipo principal da mensagem (image, video, audio, document, location)
   *  é tratado apenas no switch de renderContent; aqui cuidamos apenas de anexos adicionais.
   */
  const renderMedia = () => {
    const parts = [];
    if (message.image && String(message.image).trim() && message.type !== "image") {
      parts.push(
        <Box key="img" sx={{ mb: message.text ? 1 : 0 }}>
          <ImageMessage message={message} />
        </Box>
      );
    }
    if (message.video && String(message.video).trim() && message.type !== "video") {
      parts.push(
        <Box key="video" sx={{ mb: message.text ? 1 : 0 }}>
          <VideoMessage message={{ ...message, url: message.video || message.url }} />
        </Box>
      );
    }
    if (message.audio && String(message.audio).trim() && message.type !== "audio") {
      parts.push(
        <Box key="audio" sx={{ mb: message.text ? 1 : 0 }}>
          <AudioMessage message={message} />
        </Box>
      );
    }
    if (message.document && message.type !== "document") {
      const doc = message.document;
      const docUrl = typeof doc === "string" ? doc : (doc?.url || doc?.link);
      const docName = typeof doc === "object" && doc != null ? (doc.name || doc.fileName || doc.filename) : null;
      if (docUrl && String(docUrl).trim()) {
        parts.push(
          <Box key="doc" sx={{ mb: message.text ? 1 : 0 }}>
            <DocumentMessage message={{ ...message, url: docUrl, name: docName || "Documento" }} />
          </Box>
        );
      }
    }
    if (message.location && message.type !== "location") {
      const loc = message.location;
      const locStr = typeof loc === "string" ? loc : (loc?.address || (loc?.latitude != null && loc?.longitude != null ? `${loc.latitude}, ${loc.longitude}` : null));
      if (locStr && String(locStr).trim()) {
        parts.push(
          <Box key="loc" sx={{ mb: message.text ? 1 : 0 }}>
            <LocationMessage message={{ ...message, location: locStr }} />
          </Box>
        );
      }
    }
    if (parts.length === 0) return null;
    return <>{parts}</>;
  };

  const renderContent = () => {
    const mediaBlock = renderMedia();
    switch (message.type) {
      case "text":
        return (
          <>
            {mediaBlock}
            <TextMessage message={message} textOverride={waClientTextOverride} />
            {isLikelyWhatsapp && isClient && (
              <WhatsappInboundChoiceFooter
                message={message}
                textOverride={waClientTextOverride}
                defaultChannel={defaultChannel}
              />
            )}
            <WhatsappInteractiveBlocks
              message={message}
              defaultChannel={defaultChannel}
              isClient={isClient}
            />
          </>
        );
      case "image": {
        // Não exibir texto quando for apenas link HTML da própria imagem ou placeholder "[mensagem]"
        const imageText = message.text && String(message.text).trim();
        const isOnlyHtmlLink =
          imageText && /^\s*<a\s+href=["'][^"']+["'][^>]*>[\s\S]*?<\/a>\s*$/i.test(imageText);
        const isPlaceholderOnly = imageText === "[mensagem]";
        const showImageCaption = imageText && !isOnlyHtmlLink && !isPlaceholderOnly;
        return (
          <>
            {/* Mensagem principal do tipo imagem */}
            {message.image && String(message.image).trim() && (
              <Box sx={{ mb: showImageCaption ? 1 : 0 }}>
                <ImageMessage message={message} />
              </Box>
            )}
            {/* Anexos adicionais de mídia, se existirem */}
            {mediaBlock}
            {showImageCaption && (
              <Typography
                sx={{ mt: 0.5, "& code": { fontFamily: "monospace", fontSize: "0.9em" } }}
                component="div"
                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                dangerouslySetInnerHTML={{ __html: formatWhatsAppText(imageText) }}
              />
            )}
          </>
        );
      }
      case "audio":
        return (
          <>
            {mediaBlock}
            <AudioMessage message={message} />
          </>
        );
      case "video":
        return (
          <>
            {mediaBlock}
            <VideoMessage message={{ ...message, url: message.video || message.url }} />
          </>
        );
      case "document":
        return (
          <>
            {mediaBlock}
            {message.document && (() => {
              const doc = message.document;
              const docUrl = typeof doc === "string" ? doc : (doc?.url || doc?.link);
              const docName = typeof doc === "object" && doc != null ? (doc.name || doc.fileName || doc.filename) : "Documento";
              return docUrl ? <DocumentMessage message={{ ...message, url: docUrl, name: docName }} /> : null;
            })()}
          </>
        );
      case "location":
        return (
          <>
            {mediaBlock}
            {message.location && (() => {
              const loc = message.location;
              const locStr = typeof loc === "string" ? loc : (loc?.address || (loc?.latitude != null && loc?.longitude != null ? `${loc.latitude}, ${loc.longitude}` : null));
              return locStr ? <LocationMessage message={{ ...message, location: locStr }} /> : null;
            })()}
          </>
        );
      case "log":
        return (
          <>
            {mediaBlock}
            <LogMessage message={message} />
          </>
        );
      case "interactive":
        return (
          <>
            {mediaBlock}
            <InteractiveMessage message={message} />
          </>
        );
      case "note":
        return (
          <>
            {mediaBlock}
            {(message.text != null && String(message.text).trim() !== "") && (
              <Typography
                component="div"
                sx={{
                  color: "inherit",
                  "& code": { fontFamily: "monospace", fontSize: "0.9em" },
                }}
                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                dangerouslySetInnerHTML={{ __html: formatWhatsAppText(message.text) }}
              />
            )}
          </>
        );
      default:
        return (
          <>
            {mediaBlock}
            <Typography
              component="div"
              sx={{ "& code": { fontFamily: "monospace", fontSize: "0.9em" } }}
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              dangerouslySetInnerHTML={{
                __html: formatWhatsAppText(message.text || "Mensagem desconhecida"),
              }}
            />
          </>
        );
    }
  };

  // Para logs: exibir nome do usuário que registrou apenas se id_usuario estiver preenchido; caso contrário não mostrar nada
  const hasLogUserId =
    message.type === "log" &&
    ((message?.id_usuario != null && message?.id_usuario !== "" && Number(message?.id_usuario) !== 0) ||
      (message?.senderId != null && message?.senderId !== "" && Number(message?.senderId) !== 0));
  const displayName =
    message.type === "log"
      ? hasLogUserId
        ? (message?.senderName || message?.userName || "")
        : ""
      : isAiAgentMessage
      ? "Agente de IA"
      : isChatbotMessage
      ? "Chatbot"
      : message?.senderName ||
        (isSystem
          ? "Sistema"
          : isClient
          ? "Cliente"
          : isLoggedInUser
          ? "Você"
          : isWhatsappAppOutbound
          ? "Enviado pelo app do WhatsApp"
          : agentSideVisual
          ? "Atendente"
          : "Usuário");

  const channelLabel = getChannelLabel(message, defaultChannel);

  // Data/hora: para e-mail recebido (inbound) prioriza createdAt (recebimento); senão updatedAt/timestamp
  const displayDate = parseDisplayDate(
    message?.createdAt ||
    message?.updatedAt ||
    message?.lastUpdate ||
    message?.timestamp
  );
  const isInboundEmail = hasEmailOriginal && isClient;

  const deliveryDisplay = getDeliveryStatusDisplay(message);
  const showDeliveryStatus = shouldShowDeliveryStatus(message, isUser);
  const isReengagement = isReengagementError(deliveryDisplay?.errorReason);
  const isSending =
    message.status === "sending" || (showDeliveryStatus && deliveryDisplay.isPending);
  const isErrorStatus =
    message.status === "error" || (showDeliveryStatus && deliveryDisplay.isError);
  const canRetry =
    isLoggedInUser &&
    typeof onSendMessage === "function" &&
    (message.status === "error" || (deliveryDisplay.isError && message.text)) &&
    !isReengagement;

  const deleteDeadlineSec =
    message?.platformDeleteDeadlineUnix != null
      ? Number(message.platformDeleteDeadlineUnix)
      : null;
  const withinProviderDeleteWindow =
    deleteDeadlineSec == null ||
    !Number.isFinite(deleteDeadlineSec) ||
    Date.now() / 1000 < deleteDeadlineSec;

  React.useEffect(() => {
    if (!message?.canDeleteFromPlatform || deleteDeadlineSec == null || !Number.isFinite(deleteDeadlineSec)) {
      return undefined;
    }
    const ms = deleteDeadlineSec * 1000 - Date.now();
    if (ms <= 0) return undefined;
    const id = setTimeout(() => setDeleteWindowTick((n) => n + 1), ms + 400);
    return () => clearTimeout(id);
  }, [message?.id, message?.canDeleteFromPlatform, deleteDeadlineSec]);

  const showDeleteAction =
    Boolean(message?.canDeleteFromPlatform) &&
    withinProviderDeleteWindow &&
    isUser &&
    typeof onDeleteMessage === "function" &&
    !isSending &&
    message?.status !== "sending";

  const renderStatusIcon = () => {
    if (deliveryDisplay.icon === "pending") return <ScheduleIcon sx={{ fontSize: 14 }} />;
    if (deliveryDisplay.icon === "sent") return <CheckIcon sx={{ fontSize: 14 }} />;
    if (deliveryDisplay.icon === "delivered" || deliveryDisplay.icon === "read")
      return <DoneAllIcon sx={{ fontSize: 14 }} />;
    if (deliveryDisplay.icon === "error") return <ErrorOutlineIcon sx={{ fontSize: 14 }} />;
    return null;
  };

  const quoteInteractive =
    typeof onNavigateToQuotedMessage === "function" && quotedPreview?.matchedMessageId != null;
  let quotedPreviewBlock = null;
  if (
    quotedPreview &&
    message.type !== "log" &&
    message.type !== "note" &&
    message.type !== "Xpro"
  ) {
    const quoteBox = (
      <Box
        component={quoteInteractive ? "button" : "div"}
        type={quoteInteractive ? "button" : undefined}
        onClick={
          quoteInteractive
            ? () => onNavigateToQuotedMessage(quotedPreview.matchedMessageId)
            : undefined
        }
        sx={{
          borderLeft: "3px solid",
          borderColor: isLoggedInUser
            ? "rgba(255,255,255,0.55)"
            : agentSideVisual
            ? "rgba(0,0,0,0.22)"
            : "primary.main",
          pl: 1,
          py: 0.5,
          mb: threadHints.chips.length ? "4px" : "8px",
          mt: "-2px",
          maxWidth: "100%",
          borderRadius: "4px",
          bgcolor:
            isLoggedInUser
              ? "rgba(0,0,0,0.08)"
              : agentSideVisual
              ? "rgba(0,0,0,0.04)"
              : mode === "dark"
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.04)",
          ...(quoteInteractive
            ? {
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                font: "inherit",
                color: "inherit",
                width: "100%",
                display: "block",
                appearance: "none",
                WebkitAppearance: "none",
                transition: "opacity 0.15s ease",
                "&:hover": { opacity: 0.92 },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
              }
            : {}),
        }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "inherit",
          }}
        >
          {quotedPreview.author}
        </Typography>
        <Typography
          variant="caption"
          component="div"
          sx={{
            mt: "2px",
            lineHeight: 1.35,
            color: "inherit",
            opacity: 0.88,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
          }}
        >
          {quotedPreview.text}
        </Typography>
      </Box>
    );
    quotedPreviewBlock = quoteInteractive ? (
      <Tooltip title="Ir para a mensagem original">{quoteBox}</Tooltip>
    ) : (
      quoteBox
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: getAlignment(),
        paddingTop: "8px",
        paddingBottom: "8px",
        width: "100%",
        // Logs e notas devem ficar totalmente centralizados, sem recuos laterais extras.
        pl:
          message.type === "log" || message.type === "note" || message.type === "Xpro"
            ? 0
            : isUser
            ? "15%"
            : 0,
        pr:
          message.type === "log" || message.type === "note" || message.type === "Xpro"
            ? 0
            : isUser
            ? 0
            : "15%",
      }}
    >
      <Box sx={{ ...getMessageStyle() }}>
        {displayName && (
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: "bold",
              marginBottom: "4px",
              color:
                message.type === "log" || message.type === "note" || message.type === "Xpro"
                  ? theme.palette.text.primary
                  : isLoggedInUser
                  ? (theme.palette.messages?.userContrast ?? "#ffffff")
                  : agentSideVisual
                  ? (theme.palette.messages?.othersContrast ?? theme.palette.text.primary)
                  : isClient
                  ? (theme.palette.messages?.clientContrast ?? theme.palette.text.primary)
                  : theme.palette.text.secondary,
              textAlign:
                message.type === "log" || message.type === "note" || message.type === "Xpro"
                  ? "center"
                  : isUser
                  ? "right"
                  : "left",
            }}
          >
            {(isAiAgentMessage || isChatbotMessage) && (
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", mr: 0.75 }}>
                {isAiAgentMessage ? (
                  <SmartToyOutlinedIcon sx={{ fontSize: 16, color: "inherit" }} />
                ) : (
                  <QuestionAnswerOutlinedIcon sx={{ fontSize: 16, color: "inherit" }} />
                )}
              </Box>
            )}
            {`${displayName}${channelLabel ? ` via ${channelLabel}` : ""}`}
          </Typography>
        )}
        {waForwardLabel && message.type !== "log" && message.type !== "note" && message.type !== "Xpro" && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontWeight: 600,
              opacity: 0.92,
              mb: 0.75,
              mt: displayName ? -0.25 : 0,
              textAlign:
                message.type === "log" || message.type === "note" || message.type === "Xpro"
                  ? "center"
                  : isUser
                  ? "right"
                  : "left",
              color: "inherit",
            }}
          >
            ↪ {waForwardLabel}
          </Typography>
        )}
        {isOutboundFromWhatsappApp && (
          <Box
            sx={{
              display: "flex",
              justifyContent:
                message.type === "log" || message.type === "note" || message.type === "Xpro"
                  ? "center"
                  : isUser
                  ? "flex-end"
                  : "flex-start",
              mb: "6px",
              mt: "-2px",
            }}
          >
            <Tooltip title="Mensagem enviada fora da plataforma (direto no app do WhatsApp).">
              <Chip
                size="small"
                label="WhatsApp (app)"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  opacity: 0.95,
                  color:
                    isLoggedInUser
                      ? (theme.palette.messages?.userContrast ?? "#ffffff")
                      : agentSideVisual
                      ? (theme.palette.messages?.othersContrast ?? theme.palette.text.primary)
                      : theme.palette.text.secondary,
                  borderColor:
                    isLoggedInUser
                      ? "rgba(255,255,255,0.55)"
                      : agentSideVisual
                      ? "rgba(0,0,0,0.18)"
                      : "divider",
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Tooltip>
          </Box>
        )}
        {quotedPreviewBlock}
        {message.type !== "log" &&
          message.type !== "note" &&
          message.type !== "Xpro" &&
          threadHints.chips.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                justifyContent: isUser ? "flex-end" : "flex-start",
                mb: "6px",
                mt: isOutboundFromWhatsappApp ? 0 : "-2px",
              }}
            >
              {threadHints.chips.map((c) => (
                <Tooltip key={c.key} title={c.tooltip}>
                  <Chip
                    size="small"
                    label={c.label}
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      opacity: 0.95,
                      maxWidth: "100%",
                      color: isLoggedInUser
                        ? (theme.palette.messages?.userContrast ?? "#ffffff")
                        : agentSideVisual
                        ? (theme.palette.messages?.othersContrast ?? theme.palette.text.primary)
                        : theme.palette.text.secondary,
                      borderColor: isLoggedInUser
                        ? "rgba(255,255,255,0.55)"
                        : agentSideVisual
                        ? "rgba(0,0,0,0.18)"
                        : "divider",
                      "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          )}
        {renderContent()}

        {waReactionChips && waReactionChips.length > 0 && message.type !== "log" && message.type !== "note" && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              mt: 0.75,
              mb: 0.25,
              justifyContent: getAlignment() === "flex-end" ? "flex-end" : "flex-start",
            }}
          >
            {waReactionChips.map((c) => (
              <Tooltip key={c.key} title={c.tooltip}>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    fontSize: "1.1rem",
                    lineHeight: 1.2,
                    cursor: "default",
                    userSelect: "none",
                  }}
                >
                  {c.label}
                </Typography>
              </Tooltip>
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              message.type === "log" || message.type === "note" || message.type === "Xpro"
                ? "center"
                : "flex-end",
            gap: 0.5,
            marginTop: message.type === "log" ? "2px" : "4px",
            flexWrap: "wrap",
          }}
        >
          {hasEmailOriginal && (
            <Tooltip title="Abrir e-mail original recebido">
              <IconButton
                size="small"
                onClick={() => setEmailOriginalOpen(true)}
                sx={{
                  padding: "2px",
                  color:
                    message.type === "note" || message.type === "Xpro"
                      ? theme.palette.text.primary
                      : isLoggedInUser
                      ? (theme.palette.messages?.userContrast ?? "#ffffff")
                      : agentSideVisual
                      ? (theme.palette.messages?.othersContrast ?? theme.palette.text.primary)
                      : isClient
                      ? (theme.palette.messages?.clientContrast ?? theme.palette.text.primary)
                      : theme.palette.text.secondary,
                }}
                aria-label="Abrir e-mail original"
              >
                <MailOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {showDeleteAction && (
            <Tooltip title="Apagar no WhatsApp (Cloud API): dentro de 48h do envio, com entrega confirmada">
              <IconButton
                size="small"
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  padding: "2px",
                  color:
                    isLoggedInUser
                      ? (theme.palette.messages?.userContrast ?? "#ffffff")
                      : agentSideVisual
                      ? (theme.palette.messages?.othersContrast ?? theme.palette.text.primary)
                      : theme.palette.text.secondary,
                }}
                aria-label="Apagar mensagem"
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Typography
            variant="caption"
            sx={{
              textAlign:
                message.type === "log" || message.type === "note" || message.type === "Xpro"
                  ? "center"
                  : "right",
              color:
                message.type === "log" || message.type === "note" || message.type === "Xpro"
                  ? theme.palette.text.primary
                  : isLoggedInUser
                  ? (theme.palette.messages?.userContrast ?? "#ffffff")
                  : agentSideVisual
                  ? (theme.palette.messages?.othersContrast ?? theme.palette.text.primary)
                  : isClient
                  ? (theme.palette.messages?.clientContrast ?? theme.palette.text.primary)
                  : theme.palette.text.secondary,
              fontSize: message.type === "log" ? "0.75rem" : "0.75rem",
            }}
          >
            {isInboundEmail ? `Recebido em ${displayDate}` : displayDate}
          </Typography>
        </Box>

        {hasEmailOriginal && (
          <EmailOriginalDialog
            open={emailOriginalOpen}
            onClose={() => setEmailOriginalOpen(false)}
            rawEmail={message.emailOriginal}
          />
        )}

        <Dialog open={deleteDialogOpen} onClose={() => !deleteInProgress && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Apagar mensagem?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Só é possível quando a API da Meta aceita: em geral até 48 horas após o envio e após a mensagem ter saído do
              estado &quot;enviando&quot; (sem falha de entrega). A remoção vale também para o cliente no WhatsApp.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteInProgress}>
              Cancelar
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={deleteInProgress}
              onClick={async () => {
                if (!onDeleteMessage || message?.id == null) return;
                setDeleteInProgress(true);
                try {
                  await onDeleteMessage(message.id);
                  setDeleteDialogOpen(false);
                } catch (_) {
                  // erro tratado no container (snackbar)
                } finally {
                  setDeleteInProgress(false);
                }
              }}
            >
              {deleteInProgress ? "Apagando…" : "Apagar"}
            </Button>
          </DialogActions>
        </Dialog>

        {isSending && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginTop: "2px",
              justifyContent: "flex-end",
            }}
          >
            Enviando… <CircularProgress size={12} />
          </Typography>
        )}

        {showDeliveryStatus && !isSending && deliveryDisplay.label && (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", marginTop: "2px" }}>
        {deliveryDisplay.errorReason ? (
              <Tooltip
                title={
                  isReengagement
                    ? REENGAGEMENT_FRIENDLY_MESSAGE
                    : getFriendlyDeliveryError(
                        String(deliveryDisplay.errorReason).replace(/#\d+/g, "").trim()
                      )
                }
                placement="left"
              >
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                    color: deliveryDisplay.isError
                      ? theme.palette.error.main
                      : theme.palette.text.secondary,
                    fontSize: "0.7rem",
                  }}
                >
                  {renderStatusIcon()}
                  <span>{deliveryDisplay.label}</span>
                  <Box
                    component="span"
                    sx={{
                      opacity: 0.9,
                      maxWidth: "220px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {" · "}
                    {isReengagement
                      ? "Janela de 24h fechada. Use um template."
                      : (() => {
                          const friendly = getFriendlyDeliveryError(
                            String(deliveryDisplay.errorReason).replace(/#\d+/g, "").trim()
                          );
                          if (!friendly) return null;
                          return friendly.length > 80 ? `${friendly.slice(0, 80)}…` : friendly;
                        })()}
                  </Box>
                </Typography>
              </Tooltip>
            ) : (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  justifyContent: "flex-end",
                  color: deliveryDisplay.isError
                    ? theme.palette.error.main
                    : theme.palette.text.secondary,
                  fontSize: "0.7rem",
                }}
              >
                {renderStatusIcon()}
                <span>{deliveryDisplay.label}</span>
              </Typography>
            )}
          </Box>
        )}

        {isErrorStatus && !showDeliveryStatus && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "2px" }}
          >
            Falha ao enviar <ErrorOutlineIcon fontSize="small" />
          </Typography>
        )}
        {isErrorStatus && message?.errorMessage && (
          <Typography variant="caption" color="error" sx={{ display: "block", marginTop: "2px" }}>
            {message.errorMessage}
          </Typography>
        )}

        {canRetry && (
          <Button
            variant="text"
            size="small"
            sx={{ mt: 0.5, minWidth: 0, textTransform: "none" }}
            onClick={() =>
              onSendMessage(
                {
                  message: message.text,
                  file: message.file,
                  audioURL: message.audio,
                  channel: message.channel,
                  channelType: message.type,
                },
                message.id
              )
            }
          >
            Tentar reenviar
          </Button>
        )}
        {isErrorStatus && isReengagement && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.5,
              color: theme.palette.info.main,
              fontSize: "0.7rem",
            }}
          >
            {REENGAGEMENT_FRIENDLY_MESSAGE}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const areEqual = (prev, next) => {
  const a = prev.message || {};
  const b = next.message || {};
  const dsA = a.deliveryStatus;
  const dsB = b.deliveryStatus;
  return (
    a.id === b.id &&
    a.status === b.status &&
    a.text === b.text &&
    (a.updatedAt || a.lastUpdate || a.timestamp || a.createdAt) ===
      (b.updatedAt || b.lastUpdate || b.timestamp || b.createdAt) &&
    a.file === b.file &&
    a.audio === b.audio &&
    a.image === b.image &&
    a.video === b.video &&
    (a.json_whatsapp ?? "") === (b.json_whatsapp ?? "") &&
    (a.quotedMessageIdWhatsapp ?? "") === (b.quotedMessageIdWhatsapp ?? "") &&
    (a.isForwardedWhatsapp ?? "") === (b.isForwardedWhatsapp ?? "") &&
    (a.quotedMessageConversationWhatsapp ?? "") === (b.quotedMessageConversationWhatsapp ?? "") &&
    JSON.stringify(a.emailOriginalTags ?? null) === JSON.stringify(b.emailOriginalTags ?? null) &&
    (typeof a.document === "object" && typeof b.document === "object"
      ? JSON.stringify(a.document) === JSON.stringify(b.document)
      : a.document === b.document) &&
    (typeof a.location === "object" && typeof b.location === "object"
      ? JSON.stringify(a.location) === JSON.stringify(b.location)
      : a.location === b.location) &&
    a.channel === b.channel &&
    (dsA?.status ?? -1) === (dsB?.status ?? -1) &&
    (dsA?.label ?? "") === (dsB?.label ?? "") &&
    (dsA?.errorReason ?? "") === (dsB?.errorReason ?? "") &&
    prev.quotedLookup === next.quotedLookup &&
    prev.onNavigateToQuotedMessage === next.onNavigateToQuotedMessage &&
    prev.conversationMessages === next.conversationMessages &&
    prev.messageIndex === next.messageIndex
  );
};

export default React.memo(MessageItem, areEqual);
